import type { BrandKit, Timeline, Transcript, VorlagenArt, VorlagenDaten } from '@schnitt/shared';

/**
 * Die Marke aus einem Gast-Link, **für die Lebensdauer des Tabs** -- und
 * zwar auch über ein Neuladen hinweg (Sascha, 15. Oktober: „beim mit
 * Schneide Link kommt immer wieder plötzlich der Login Screen“).
 *
 * Sie wird aus der Adresszeile genommen, damit sie in keinem Bildschirmfoto
 * und keinem weitergegebenen Link landet. Bis dahin lebte sie danach nur im
 * Speicher der Seite: jedes Neuladen -- und Safari lädt einen Tab mit viel
 * Video im Hintergrund von selbst neu -- liess die Adresse ohne Marke
 * zurück, und der Gast stand vor der Anmeldung. Jetzt liegt sie zusätzlich
 * im `sessionStorage`: der gilt nur in diesem Tab, übersteht das Neuladen
 * und ist weg, wenn der Tab zugeht.
 */
const GAST_SCHLUESSEL = 'schnitt-gast-link';

/**
 * **Und je Projekt über den Tab hinaus** (Sascha, 16. Oktober: „Leider lockt
 * es Leute immer noch aus, die einen Mitarbeit-Link haben ohne Account“).
 * Der Tab allein reichte nicht: wer ihn schliesst und das Projekt später aus
 * dem Verlauf oder vom Home-Bildschirm öffnet, kommt mit der Adresse ohne
 * Marke -- und stand vor der Anmeldung. Darum merkt sich das Gerät die Marke
 * zum Projekt (`/p/<slug>`). Gilt sie nicht mehr, wird sie vergessen.
 */
const GAST_LINKS = 'schnitt-gast-links';

function gastLinks(): Record<string, string> {
  try {
    const roh = JSON.parse(localStorage.getItem(GAST_LINKS) ?? '{}') as unknown;
    return roh && typeof roh === 'object' && !Array.isArray(roh) ? (roh as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function schreibeGastLinks(links: Record<string, string>): void {
  try {
    if (Object.keys(links).length) localStorage.setItem(GAST_LINKS, JSON.stringify(links));
    else localStorage.removeItem(GAST_LINKS);
  } catch {
    // Ohne Speicher bleibt es beim Tab.
  }
}

/** Die Marke für dieses Projekt, gemerkt von einem früheren Besuch. */
export function gastLinkFuer(slug: string): string | null {
  const t = gastLinks()[slug];
  return typeof t === 'string' && t ? t : null;
}

export function merkeGastLink(slug: string, token: string): void {
  const links = gastLinks();
  if (links[slug] === token) return;
  links[slug] = token;
  schreibeGastLinks(links);
}

function vergissGastLink(token: string): void {
  const links = gastLinks();
  const rest = Object.fromEntries(Object.entries(links).filter(([, t]) => t !== token));
  if (Object.keys(rest).length !== Object.keys(links).length) schreibeGastLinks(rest);
}

let shareToken: string | null = (() => {
  try {
    return sessionStorage.getItem(GAST_SCHLUESSEL);
  } catch {
    return null;
  }
})();

export function setShareToken(token: string | null): void {
  // Eine Marke, die verworfen wird, gilt nicht mehr -- auch nicht im
  // Gedächtnis des Geräts.
  if (!token && shareToken) vergissGastLink(shareToken);
  shareToken = token;
  try {
    if (token) sessionStorage.setItem(GAST_SCHLUESSEL, token);
    else sessionStorage.removeItem(GAST_SCHLUESSEL);
  } catch {
    // Ohne Speicher (privates Fenster mit strengen Regeln) bleibt es beim
    // Speicher der Seite -- wie vorher.
  }
}

export function getShareToken(): string | null {
  return shareToken;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  // Bei FormData setzt der Browser den Typ samt Grenzmarke selbst; ein
  // eigener content-type macht daraus einen Körper, den niemand lesen kann.
  if (init.body && !(init.body instanceof FormData)) headers['content-type'] = 'application/json';
  if (shareToken) headers['x-share-token'] = shareToken;

  const res = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const text = await res.text();
  // Eine Fehlerseite ist kein JSON -- nginx antwortet während des Ausspielens
  // mit HTML. Das ist dann ein Fehler mit seiner Nummer, kein Rätsel.
  let body: unknown = null;
  try {
    body = text ? (JSON.parse(text) as unknown) : null;
  } catch (err) {
    if (res.ok) throw err;
  }
  if (!res.ok) {
    const message = (body as { error?: string } | null)?.error ?? `Fehler ${res.status}`;
    throw new ApiError(res.status, message);
  }
  return body as T;
}

export type ProjectRole = 'owner' | 'editor' | 'viewer';

export interface User {
  userId: string;
  email: string;
  name: string;
  role: 'admin' | 'member';
  /** Die selbst gewählte Farbe, oder leer für „rechne sie aus“ (7.45). */
  color?: string;
  /** Wann das Profilbild zuletzt gesetzt wurde, oder null (7.45b). */
  avatarAt?: string | null;
  shareRole?: 'owner' | 'editor' | 'viewer';
}

export interface ProjectSummary {
  id: string;
  slug: string;
  name: string;
  box_folder_id: string;
  archived: boolean;
  updated_at: string;
  role: 'owner' | 'editor' | 'viewer';
  asset_count: number;
  permalink: string;
  folder: string;
  posterUrl: string | null;
  width: number;
  height: number;
  /** Gesetzt, solange die Originale nur in der Ablage liegen (7.51). */
  ausgelagert_am?: string | null;
  /** Hängt an einem Ordner der Ablage (7.217). */
  ablage?: boolean;
}

export interface ProjectDetail {
  folder: string;
  id: string;
  slug: string;
  name: string;
  boxFolderId: string | null;
  boxWorkFolderId: string | null;
  boxWebhookId: string | null;
  brandKitId: string | null;
  brandKit: BrandKit;
  /** Signed url for the kit's logo, or null when none is uploaded. */
  brandLogoUrl: string | null;
  rev: number;
  archived: boolean;
  timeline: Timeline;
  permalink: string;
  updatedAt: string;
  /** Wann angelegt (7.232). */
  createdAt?: string;
  /** Der Ordner der Ablage, an dem das Projekt hängt (7.50) -- null heisst: keiner. */
  ablage: { anbieter: string | null; ordner: string; rohdaten: string | null } | null;
  /** Gesetzt, solange die Originale nur in der Ablage liegen (7.51). */
  ausgelagertAm: string | null;
  /** Notizen zum Projekt (S26). */
  notizen: string;
}

export interface AssetView {
  id: string;
  boxFileId: string;
  name: string;
  kind: 'video' | 'audio' | 'image';
  durationSec: number;
  width: number;
  height: number;
  fps: number;
  hasAudio: boolean;
  /** Wie viel nach Sprache aussieht, 0..1 (4. November); `null`: noch nicht gemessen. */
  sprachanteil?: number | null;
  status: 'pending' | 'ingesting' | 'ready' | 'failed';
  sizeBytes: number;
  error: string | null;
  updatedAt: string | null;
  proxyUrl: string | null;
  posterUrl: string | null;
  filmstripUrl: string | null;
  filmstripGrid: { frames: number; columns: number; rows: number; intervalSec: number } | null;
  originalUrl: string | null;
  waveformUrl: string | null;
  /** Wo die Einstellung wechselt (7.66d). `null`: noch nicht erkannt. */
  szenenUrl: string | null;
}

export interface VorlageView {
  id: string;
  art: VorlagenArt;
  name: string;
  /** `null`, wenn eine ältere Fassung sie geschrieben hat und wir sie nicht mehr lesen (S24). */
  daten: VorlagenDaten | null;
  satz: string;
  updatedAt: string | null;
}

export interface RenderView {
  id: string;
  /** `bild` ist ein Einzelbild aus dem Schnitt (S22), kein Film. */
  kind: 'preview' | 'final' | 'bild';
  status: 'queued' | 'running' | 'ready' | 'failed' | 'cancelled';
  progress: number;
  rev: number;
  box_file_id: string | null;
  duration_sec: number | null;
  size_bytes: number | null;
  /** Tonmessgeräte (S18): gemessen, sobald der Film fertig ist. Null bei einer Vorschau oder ohne Ton. */
  lufs: number | null;
  lra: number | null;
  spitze_dbtp: number | null;
  warnings: string[];
  error: string | null;
  created_at: string;
  finished_at: string | null;
  /** Set once the clean-up removed the file; the row stays as the record of the export. */
  cleaned_at: string | null;
  url: string | null;
  /** Die Untertitel neben dem Film (S21), oder null -- eingebrannt zählt hier nicht. */
  untertitelUrl: string | null;
  /** Welche Fassung das ist (S29) -- der Name der Export-Vorlage, oder null. */
  fassung: string | null;
  /** 1-based place among everything waiting, or null once it is not waiting. */
  queuePosition: number | null;
  /** Seconds until this one starts, or null while there is nothing to measure. */
  queueEtaSec: number | null;
  /**
   * Ob er steht (7.40b): seit wie vielen Sekunden, und womit gerechnet wurde.
   * `null` heisst: alles im Rahmen — auch bei einem Film, der lange braucht.
   */
  stillstand?: { seit: number; erwartet: number | null } | null;
  /** Der Abdruck des Abschnitts, aus dem er gerechnet wurde -- null beim ganzen Film (7.71b). */
  abdruck?: string | null;
  /** Von wann bis wann auf der Zeitleiste, wenn es ein Abschnitt ist. */
  von?: number | null;
  bis?: number | null;
}

export interface QueueState {
  running: number;
  waiting: number;
  /** Seconds of wall clock per second of film, measured on finished exports. */
  secondsPerSecond: number | null;
}

export interface AccountView {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'member';
  disabled: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface BoxSettingsView {
  source: 'datenbank' | 'umgebung' | 'keine';
  clientId: string;
  subjectType: string;
  subjectId: string;
  rootFolderId: string;
  hasClientSecret: boolean;
  hasDeveloperToken: boolean;
  hasWebhookKeys: boolean;
  unreadable: boolean;
}

export interface NextcloudSettingsView {
  quelle: 'datenbank' | 'umgebung' | 'keine';
  url: string;
  benutzer: string;
  wurzel: string;
  hatPasswort: boolean;
  unlesbar: boolean;
}

export interface AblageBefund {
  ok: boolean;
  anbieter: string;
  wurzel: string;
  schritt: 'lesen' | 'schreiben' | 'zuruecklesen' | 'vergleichen' | 'aufraeumen' | 'fertig';
  eintraege?: number;
  satz: string;
  status?: number;
}

export interface CleanupReport {
  at: string;
  retentionDays: number;
  freedBytes: number;
  exports: number;
  previews: number;
  work: number;
  orphans: number;
  /** Abgelaufene Einladungen und Passwort-Links. */
  marken: number;
  /** Fehlerprotokolle, die älter waren als das Nachsehen. */
  fehler: number;
  errors: string[];
}

export interface TabellenNutzung {
  tabelle: string;
  zeilen: number;
  bytes: number;
  was: string;
  art: 'geraeumt' | 'waechst' | 'bestand';
}

export interface StorageView {
  originals: number;
  proxies: number;
  exports: number;
  work: number;
  other: number;
  total: number;
  files: number;
  freeBytes: number | null;
  lastCleanup: CleanupReport | null;
  retentionDays: number;
  scheduled: boolean;
  backups: { configured: boolean; count: number; latestAt: string | null; latestBytes: number; totalBytes: number };
  datenbank: TabellenNutzung[];
  datenbankBytes: number;
}

export interface FailureView {
  id: string;
  kind: 'ingest' | 'render' | 'transcribe';
  project_id: string | null;
  project_name: string | null;
  ref_id: string | null;
  name: string;
  message: string;
  stderr: string;
  probe: {
    format: string;
    durationSec: number;
    sizeBytes: number;
    streams: { index: number; type: string; codec: string; profile?: string; pixFmt?: string; width?: number; height?: number; colour?: string; handler?: string; sideData?: string[]; rotation?: number }[];
    error?: string;
  } | null;
  args: string[] | null;
  ffmpeg: string | null;
  created_at: string;
}

/** Eine Zeile aus den Gerätebefunden (7.69j). */
export interface MesswertZeile {
  id: string;
  created_at: string;
  ua: string;
  daten: {
    anlass?: string;
    modus?: string;
    kopfProSekunde?: number;
    /** Millisekunden vom Tastendruck bis zur ersten Bewegung; null heisst: nichts bewegt (7.69v). */
    anlaufMs?: number | null;
    takt?: string | null;
    sekunden?: number;
    puffer?: { haltungen?: number; msGesamt?: number; msMax?: number; abgelaufen?: number };
    aufsBild?: { haltungen?: number; msGesamt?: number; msMax?: number; abgelaufen?: number };
    ram?: { dateien?: number; bytes?: number; inGebrauch?: number; laufend?: number };
    [k: string]: unknown;
  };
}

export interface DiagnosisView {
  at: string;
  version: { commit: string; builtAt: string };
  ffmpeg: string;
  settings: { publicUrl: string; boxConfigured: boolean; mailConfigured: boolean; mediaRetentionDays: number; cleanupIntervalHours: number; uploadMaxMb: number };
  remoteKey: { fromEnv: boolean; stored: string | null };
  workers: { key: string; at: string; ageSeconds: number; host: string; pid: number; queues: string[]; ffmpeg: string; startedAt: string; active?: { queue: string; id: string; since: string }[] }[];
  queues: Record<
    string,
    {
      /** Neben den Zählern von BullMQ steht hier `failedOffen`: was noch keiner behoben hat. */
      counts: Record<string, number>;
      failed: { id: string; reason: string; attempts: number; at: string | null; erledigt?: boolean }[];
    }
  >;
  stuck: { kind: string; id: string; project_id: string; name: string; status: string; minutes: number }[];
  failures: FailureView[];
  storage: StorageView;
}

/** Ein Hinweis, der an einer Stelle im Schnitt hängt. */
export interface KommentarView {
  id: string;
  parent_id: string | null;
  author_id: string | null;
  author_name: string;
  at_seconds: number;
  /** Der Clip, an dem der Hinweis klebt -- oder `null` für einen Zeitpunkt. */
  clip_id: string | null;
  body: string;
  resolved_at: string | null;
  created_at: string;
  /** Ob, wer fragt, ihn löschen darf -- der eigene, oder als Eigentümerin jeder (7.273). */
  darf_loeschen?: boolean;
  /** Ob, wer fragt, ihn ändern darf -- nur den eigenen (7.275). */
  darf_aendern?: boolean;
  /** Wann zuletzt geändert, oder null. */
  edited_at?: string | null;
  /** Gelöscht, aber mit Antworten -- er bleibt leer stehen (7.278). */
  deleted_at?: string | null;
}

/** Eine Abnahme hängt an der Fassung, die freigegeben wurde -- nicht am Projekt. */
export interface AbnahmeView {
  id: string;
  rev: number;
  by_user_id: string | null;
  by_name: string;
  note: string;
  created_at: string;
  withdrawn_at: string | null;
}

/** Ein Schritt im Verlauf: eine Fassung, eine Person, ein Satz dazu. */
export interface VerlaufSchritt {
  rev: number;
  label: string;
  actor: string | null;
  createdAt: string;
}

export interface MailSettingsView {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  fromAddress: string;
  fromName: string;
  hasPassword: boolean;
  configured: boolean;
  unreadable: boolean;
}

/** Verwaltung → KI (7.66): was die Seite sieht -- ohne die Schlüssel selbst. */
export interface KiEinstellungenView {
  transkription: { anbieter: 'elevenlabs'; adresse: string; hatSchluessel: boolean; eingerichtet: boolean };
  modell: { anbieter: 'claude' | 'ollama' | 'openai'; adresse: string; name: string; hatSchluessel: boolean; eingerichtet: boolean };
  unlesbar: boolean;
}

/** Ein Vorschlag des KI-Schnitts (7.66) -- noch kein Schnitt, eine Liste zum Prüfen. */
export interface KiVorschlagView {
  vorlage: string;
  modus: 'herausnehmen' | 'auswaehlen';
  zielSekunden: number | null;
  abschnitte: { id: string; medium: string; name: string; von: number; bis: number; text: string; sprecher: string | null; einstellung: number }[];
  begruendung: string;
  dauer: number;
  verworfen: string[];
  gesamt: number;
}

export interface InvitationView {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'member';
  expires_at: string;
  created_at: string;
}

export interface ChangelogEntry {
  /** 1.0, 1.1, … -- ältere Ausgaben haben keine (28. Oktober). */
  version: string | null;
  date: string;
  title: string;
  body: string;
  /** Was dahintersteckt -- kommt nur bei Admins gefüllt an (7.84). */
  dahinter: string;
  /** Der Satz unter der Überschrift; den bekommen alle. */
  satz: string;
  /**
   * Die zwei Listen einer Ausgabe -- was neu ist, was behoben wurde
   * (18. September). Ausgaben von vorher haben sie nicht; dann sind sie leer
   * und es bleibt beim Satz.
   */
  neu: string[];
  fixes: string[];
}

export interface VersionInfo {
  /** Identifies the running build; changes exactly when the image changes. */
  build: string;
  commit: string | null;
  builtAt: string | null;
  entries: ChangelogEntry[];
  /** Die Versionsnummer, 1.0, 1.1, … -- oder vor 1.0 der Zeitstempel des Baus. */
  release: string;
  /** Der Zeitstempel des Baus, 2026.10.29.00.12; ohne Bau `null`. */
  bau?: string | null;
}

export interface TranscriptSummary {
  id: string;
  asset_id: string | null;
  asset_name: string | null;
  language: string;
  provider: string;
  status: 'pending' | 'running' | 'ready' | 'failed';
  error: string | null;
  created_at: string;
  /** Ob die Abschrift steht (7.40c) -- dieselbe Regel wie beim Export. */
  stillstand?: { seit: number; erwartet: number | null } | null;
}

export interface BrandKitView {
  id: string;
  name: string;
  data: BrandKit;
  logoUrl: string | null;
  fontUrl: string | null;
  updatedAt: string | null;
  /** Wie viele Projekte es verwenden -- nur in der Liste (7.219). */
  projekte?: number;
}

export interface RoadmapItem {
  id: string;
  key: string | null;
  title: string;
  detail: string;
  status: 'vorschlag' | 'offen' | 'laeuft' | 'erledigt' | 'abgelehnt' | 'entfallen';
  rank: number;
  area: string;
  imageUrl: string | null;
  createdAt: string;
  author: string | null;
  urgent: boolean;
  up: number;
  down: number;
  /** Die eigene Stimme: 1, -1 oder 0. */
  myVote: number;
  comments: number;
}

export interface RoadmapComment {
  id: string;
  author: string;
  text: string;
  createdAt: string;
}

/** Ein Schlüssel für Werkzeuge (7.67), wie ihn die Liste zeigt -- ohne den Schlüssel selbst. */
export interface SchluesselView {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

export const api = {
  me: () => request<{ user: User; installation?: { box: boolean } }>('/api/auth/me'),
  // 7.67: Schlüssel für Werkzeuge von aussen (MCP). Der Schlüssel selbst
  // kommt nur beim Anlegen zurück.
  schluessel: () => request<{ schluessel: SchluesselView[] }>('/api/auth/me/schluessel'),
  schluesselAnlegen: (name: string) =>
    request<{ schluessel: { id: string; name: string; token: string } }>('/api/auth/me/schluessel', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  schluesselLoeschen: (id: string) => request<{ ok: true }>(`/api/auth/me/schluessel/${id}`, { method: 'DELETE' }),
  version: () => request<VersionInfo>('/api/version'),

  nextcloudSettings: () => request<{ nextcloud: NextcloudSettingsView }>('/api/settings/nextcloud'),
  saveNextcloudSettings: (input: { url: string; benutzer: string; passwort: string; wurzel: string }) =>
    request<{ nextcloud: NextcloudSettingsView }>('/api/settings/nextcloud', { method: 'PUT', body: JSON.stringify(input) }),
  clearNextcloudSettings: () =>
    request<{ nextcloud: NextcloudSettingsView }>('/api/settings/nextcloud', { method: 'DELETE' }),
  pruefeAblage: () => request<{ befund: AblageBefund }>('/api/settings/ablage/pruefen', { method: 'POST' }),
  boxSettings: () => request<{ box: BoxSettingsView }>('/api/settings/box'),
  ensureBoxWebhook: (projectId: string) =>
    request<{ webhookId: string; created: boolean }>(`/api/projects/${projectId}/webhook`, { method: 'POST' }),
  removeBoxWebhook: (projectId: string) => request<{ ok: true }>(`/api/projects/${projectId}/webhook`, { method: 'DELETE' }),
  saveBoxSettings: (input: Record<string, string>) =>
    request<{ box: BoxSettingsView }>('/api/settings/box', { method: 'PUT', body: JSON.stringify(input) }),
  clearBoxSettings: () => request<{ box: BoxSettingsView }>('/api/settings/box', { method: 'DELETE' }),
  testBoxSettings: (input: Record<string, string>) =>
    request<{ ok: boolean; message: string }>('/api/settings/box/test', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  diagnose: () => request<{ diagnosis: DiagnosisView }>('/api/diagnose'),
  diagnoseAssets: () =>
    request<{ assets: { id: string; name: string; project_id: string; project_name: string; status: string; minutes: number; size_bytes: number; error: string | null }[] }>('/api/diagnose/assets'),
  /** Erledigte Fehlschläge aus den Warteschlangen räumen. */
  altlastenAufraeumen: () =>
    request<{ entfernt: number; geblieben: number }>('/api/diagnose/aufraeumen', { method: 'POST' }),
  requeueStuck: () =>
    request<{ report: { assets: { id: string; name: string }[]; transcripts: { id: string }[]; rendersStuck: { id: string }[] } }>('/api/diagnose/requeue', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  /**
   * Was die Geräte beim Abspielen selbst geschickt haben (7.69j/7.69r).
   *
   * Ohne diesen Weg müsste Sascha mir einen Schlüssel geben, damit ich die
   * Zahlen lesen kann -- Schlüssel gehören aber nicht in einen Chat. Als
   * Admin angemeldet sieht er sie hier und kann sie mir vorlesen.
   */
  geraetebefunde: (limit = 12) =>
    request<{ messwerte: MesswertZeile[] }>(`/api/diagnose/messwerte?limit=${limit}`),
  createDiagnoseToken: () => request<{ token: string; createdAt: string }>('/api/diagnose/token', { method: 'POST', body: JSON.stringify({}) }),
  revokeDiagnoseToken: () => request<{ ok: true }>('/api/diagnose/token', { method: 'DELETE' }),
  diagnoseText: async () => {
    const res = await fetch('/api/diagnose?format=text', { credentials: 'include' });
    if (!res.ok) throw new ApiError(res.status, 'Der Bericht konnte nicht geladen werden.');
    return res.text();
  },
  storageUsage: () => request<{ storage: StorageView }>('/api/settings/storage'),
  cleanupStorage: () =>
    request<{ report: CleanupReport; storage: StorageView }>('/api/settings/storage/cleanup', { method: 'POST' }),
  mailSettings: () => request<{ mail: MailSettingsView }>('/api/settings/mail'),
  saveMailSettings: (input: Record<string, unknown>) =>
    request<{ mail: MailSettingsView }>('/api/settings/mail', { method: 'PUT', body: JSON.stringify(input) }),
  clearMailSettings: () => request<{ mail: MailSettingsView }>('/api/settings/mail', { method: 'DELETE' }),
  testMailSettings: (input: Record<string, unknown>) =>
    request<{ ok: boolean; message: string }>('/api/settings/mail/test', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  kiSchnitt: (
    projectId: string,
    body: { vorlageId: string; briefing: string; assetIds: string[]; vorher?: { abschnitte: string[]; rueckmeldung: string } },
  ) => request<{ vorschlag: KiVorschlagView }>(`/api/projects/${projectId}/ki-schnitt`, { method: 'POST', body: JSON.stringify(body) }),
  kiEinstellungen: () => request<{ ki: KiEinstellungenView }>('/api/settings/ki'),
  kiEinstellungenSichern: (eingabe: unknown) =>
    request<{ ki: KiEinstellungenView }>('/api/settings/ki', { method: 'PUT', body: JSON.stringify(eingabe) }),
  kiEinstellungenLoeschen: () => request<{ ki: KiEinstellungenView }>('/api/settings/ki', { method: 'DELETE' }),
  kiPruefen: (eingabe: unknown) =>
    request<{ ok: boolean; meldung: string }>('/api/settings/ki/pruefen', { method: 'POST', body: JSON.stringify(eingabe) }),

  invitations: () => request<{ invitations: InvitationView[] }>('/api/invitations'),
  invite: (input: { email: string; name: string; role: 'admin' | 'member' }) =>
    request<{ invited: string; expiresAt: string }>('/api/invitations', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeInvitation: (id: string) => request<{ ok: true }>(`/api/invitations/${id}`, { method: 'DELETE' }),
  invitation: (token: string) =>
    request<{ email: string; name: string; expiresAt: string }>(`/api/invitations/${encodeURIComponent(token)}`),
  acceptInvitation: (token: string, input: { name: string; password: string }) =>
    request<{ user: User }>(`/api/invitations/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  forgotPassword: (email: string) =>
    request<{ ok: true }>('/api/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  resetToken: (token: string) =>
    request<{ email: string; expiresAt: string }>(`/api/passwort/${encodeURIComponent(token)}`),
  resetPassword: (token: string, password: string) =>
    request<{ user: User }>(`/api/passwort/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  accounts: () => request<{ users: AccountView[] }>('/api/users'),
  createAccount: (input: { email: string; name: string; password: string; role: 'admin' | 'member' }) =>
    request<{ user: AccountView }>('/api/users', { method: 'POST', body: JSON.stringify(input) }),
  setAccountRole: (id: string, role: 'admin' | 'member') =>
    request<{ ok: true }>(`/api/users/${id}/role`, { method: 'POST', body: JSON.stringify({ role }) }),
  setAccountDisabled: (id: string, disabled: boolean) =>
    request<{ ok: true }>(`/api/users/${id}/disabled`, { method: 'POST', body: JSON.stringify({ disabled }) }),
  setAccountPassword: (id: string, password: string) =>
    request<{ ok: true }>(`/api/users/${id}/password`, { method: 'POST', body: JSON.stringify({ password }) }),
  login: (email: string, password: string) =>
    request<{ user: User }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  projects: (archived = false) =>
    request<{ projects: ProjectSummary[] }>(`/api/projects${archived ? '?archived=true' : ''}`),
  /**
   * Durch die Ordner der Ablage blättern (7.50) -- nur Ordner: hier wird ein
   * Ort ausgesucht, nicht Material.
   */
  ablageOrdner: (id?: string) =>
    request<{
      anbieter: string;
      id: string;
      wurzel: string;
      ordner: { id: string; name: string }[];
      dateien: number;
      inhalt: { id: string; name: string; groesse: number | null }[];
    }>(`/api/ablage/ordner${id === undefined ? '' : `?id=${encodeURIComponent(id)}`}`),
  /** Material aus einem anderen Ordner der Ablage in dieses Projekt holen (7.11). */
  medienHolen: (projectId: string, dateien: { id: string; name: string }[]) =>
    request<{ geholt: { id: string; name: string }[]; uebersprungen: { name: string; grund: string }[] }>(
      `/api/projects/${projectId}/medien/holen`,
      { method: 'POST', body: JSON.stringify({ dateien }) },
    ),
  /** Diesen Ordner mit dem Projekt verbinden; „rohdaten“ entsteht dabei. */
  /** Ein neuer Ordner mit dem Namen des Projekts, gleich verbunden (7.223). */
  ablageNeu: (projectId: string, worin: string) =>
    request<{ ablage: { anbieter: string; ordner: string; name: string } }>(`/api/projects/${projectId}/ablage/neu`, {
      method: 'POST',
      body: JSON.stringify({ worin }),
    }),
  ablageVerbinden: (projectId: string, ordnerId: string) =>
    request<{ ablage: { anbieter: string; ordner: string; name: string; rohdaten: string } }>(
      `/api/projects/${projectId}/ablage`,
      { method: 'POST', body: JSON.stringify({ ordnerId }) },
    ),
  ablageLoesen: (projectId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/ablage`, { method: 'DELETE' }),
  /** Was im Rohdaten-Ordner liegt und was davon die App schon kennt. */
  ablageStand: (projectId: string) =>
    request<{
      ablage: {
        anbieter: string | null;
        ordner: string;
        rohdaten: string | null;
        /**
         * Fehlt, wenn für den Anbieter keine Zugangsdaten (mehr) eingetragen
         * sind -- dann steht statt der Liste `eingerichtet: false` und ein
         * Hinweis da. Bis zum 18. September war das Feld hier als „immer da“
         * beschrieben, und das Blatt las `dateien.length`: wer seine
         * Zugangsdaten entfernte, bekam ein leeres Projekt-Blatt statt der
         * Auskunft (7.112).
         */
        dateien?: { id: string; name: string; groesse: number | null; bekannt: boolean }[];
        neu?: number;
        /** `false`: für diesen Anbieter sind keine Zugangsdaten eingetragen. */
        eingerichtet?: boolean;
        /** Was in diesem Fall dasteht -- und wo man es einträgt. */
        hinweis?: string;
        /**
         * Was oben gesichert ist (7.222): die Arbeitsdatei mit ihrer Zeit,
         * die Datei für Final Cut, die Medien und die Exporte.
         */
        sicherung?: {
          schnittdatei: string | null;
          fcpxml: boolean;
          medien: number;
          oben: number;
          fehlen: string[];
          exporte: number;
        } | null;
        /** Gesetzt, wenn die Ablage gerade nicht antwortet -- verbunden ist der Ordner trotzdem. */
        fehler?: string | null;
      } | null;
    }>(`/api/projects/${projectId}/ablage`),
  /**
   * Nachsehen, ob sich das Projekt auslagern lässt (7.51) -- nur nachsehen.
   * `nachrechnen: false` lässt die teure Prüfung (Datei oben lesen) aus.
   */
  auslagernPruefen: (projectId: string, nachrechnen = true) =>
    request<{
      befund: {
        sicher: boolean;
        platz: number;
        satz: string;
        dateien: { assetId: string; name: string; hier: number | null; beweis: { sicher: boolean; weg: string; grund: string } }[];
      };
    }>(`/api/projects/${projectId}/auslagern?nachrechnen=${nachrechnen ? 'ja' : 'nein'}`),
  auslagern: (projectId: string) =>
    request<{ entfernt: number; platz: number }>(`/api/projects/${projectId}/auslagern`, { method: 'POST' }),
  zurueckholen: (projectId: string) =>
    request<{ geholt: number; fehlen: string[] }>(`/api/projects/${projectId}/zurueckholen`, { method: 'POST' }),
  /** Den Schnitt als Datei in den Projektordner schreiben. */
  ablageSchnittSchreiben: (projectId: string) =>
    request<{ datei: string; id: string; rev: number }>(`/api/projects/${projectId}/ablage/schnitt`, { method: 'POST' }),
  createProject: (input: { name: string; boxFolderId?: string; width?: number; height?: number; fps?: number; magnetisch?: boolean; ablageOrdner?: boolean }) =>
    request<{ project: ProjectSummary }>('/api/projects', { method: 'POST', body: JSON.stringify(input) }),
  project: (id: string) => request<{ project: ProjectDetail; role: string }>(`/api/projects/${id}`),
  projectBySlug: (slug: string) =>
    request<{ project: ProjectDetail; role: string; gastName?: string }>(`/api/projects/by-slug/${encodeURIComponent(slug)}`),
  setzeProfilbild: async (bild: Blob): Promise<{ user: User }> => {
    const form = new FormData();
    form.append('bild', bild, 'profilbild.png');
    const antwort = await fetch('/api/auth/me/bild', { method: 'POST', body: form, credentials: 'same-origin' });
    if (!antwort.ok) {
      const text = await antwort.text().catch(() => '');
      let satz = `Das Bild ging nicht durch (${antwort.status}).`;
      try {
        satz = (JSON.parse(text) as { error?: string }).error ?? satz;
      } catch {
        /* dann eben der Satz oben */
      }
      throw new Error(satz);
    }
    return (await antwort.json()) as { user: User };
  },
  loescheProfilbild: () => request<{ user: User }>('/api/auth/me/bild', { method: 'DELETE' }),
  updateMe: (aenderung: { name?: string; color?: string }) =>
    request<{ user: User }>('/api/auth/me', { method: 'PATCH', body: JSON.stringify(aenderung) }),
  changePassword: (current: string, next: string) =>
    request<{ ok: true }>('/api/auth/password', { method: 'POST', body: JSON.stringify({ current, next }) }),
  setProjectFolder: (id: string, folder: string) =>
    request<{ project: ProjectDetail }>(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ folder }) }),
  renameProject: (id: string, name: string) =>
    request<{ project: ProjectDetail }>(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  setProjectArchived: (id: string, archived: boolean) =>
    request<{ project: ProjectDetail }>(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ archived }) }),
  /** Endgültig, und nur was archiviert ist (7.102). */
  deleteProject: (id: string) => request<{ ok: true; name: string }>(`/api/projects/${id}`, { method: 'DELETE' }),
  setProjectBrandKit: (id: string, brandKitId: string | null) =>
    request<{ project: ProjectDetail }>(`/api/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ brandKitId }),
    }),
  createBrandKit: (name: string) =>
    request<{ brandKit: BrandKitView }>('/api/brand-kits', { method: 'POST', body: JSON.stringify({ name }) }),
  syncBox: (id: string) =>
    request<{ added: number; skipped: number; total: number }>(`/api/projects/${id}/sync`, { method: 'POST' }),
  saveToBox: (id: string) =>
    request<{ boxFileId: string; name: string }>(`/api/projects/${id}/save-to-box`, { method: 'POST' }),

  assets: (projectId: string) => request<{ assets: AssetView[] }>(`/api/projects/${projectId}/assets`),
  /**
   * Uploading uses XMLHttpRequest rather than fetch, for the one thing fetch
   * still cannot do: report how far a request body has got. A phone sending a
   * gigabyte over mobile data without a progress bar looks broken.
   */
  uploadAsset: (projectId: string, file: File, onProgress: (fraction: number) => void) =>
    new Promise<{ asset: AssetView }>((resolve, reject) => {
      const form = new FormData();
      form.append('file', file, file.name);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/projects/${projectId}/assets`);
      if (shareToken) xhr.setRequestHeader('x-share-token', shareToken);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          body = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(body as { asset: AssetView });
        else reject(new ApiError(xhr.status, (body as { error?: string } | null)?.error ?? `Fehler ${xhr.status}`));
      };
      xhr.onerror = () => reject(new ApiError(0, 'Die Verbindung ist abgebrochen.'));
      xhr.onabort = () => reject(new ApiError(0, 'Upload abgebrochen.'));
      xhr.send(form);
    }),
  /**
   * Ein Standbild aus einem Video an der Stelle `at` (S8). Heraus kommt ein
   * gewöhnliches Bild-Medium, das gleich noch aufbereitet wird.
   */
  standbild: (projectId: string, assetId: string, at: number) =>
    request<{ asset: AssetView }>(`/api/projects/${projectId}/standbild`, {
      method: 'POST',
      body: JSON.stringify({ assetId, at }),
    }),
  reingest: (assetId: string) => request<{ ok: true }>(`/api/assets/${assetId}/reingest`, { method: 'POST' }),
  renameAsset: (assetId: string, name: string) =>
    request<{ asset: AssetView }>(`/api/assets/${assetId}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  deleteAsset: (assetId: string) => request<{ ok: true }>(`/api/assets/${assetId}`, { method: 'DELETE' }),
  /** Eine unterschriebene Adresse für das ZIP der Medien, so wie die Liste gerade gefiltert ist. */
  medienZip: (projectId: string, auswahl: { art: 'all' | 'video' | 'audio' | 'image'; suche: string }) =>
    request<{ url: string; anzahl: number; groesse: number }>(`/api/projects/${projectId}/assets/zip`, {
      method: 'POST',
      body: JSON.stringify(auswahl),
    }),
  workerHealth: () =>
    request<{ alive: boolean; workers: number; lastSeenAt: string | null; lastError: { at: string; message: string; error: string } | null }>(
      '/api/worker-health',
    ),

  renders: (projectId: string) =>
    request<{ renders: RenderView[]; queue: QueueState }>(`/api/projects/${projectId}/renders`),
  startRender: (
    projectId: string,
    kind: 'preview' | 'final',
    deliverToBox: boolean,
    /** Nur einen Bereich rechnen (S5); `null` heisst: den ganzen Schnitt. */
    bereich: { von: number; bis: number } | null = null,
    /** Wie gut ein Export wird (7.213). */
    guete: 'standard' | 'hoch' | 'master' = 'standard',
  ) =>
    request<{ renderId: string }>(`/api/projects/${projectId}/renders`, {
      method: 'POST',
      body: JSON.stringify({ kind, deliverToBox, bereich, guete }),
    }),
  /**
   * Ein Einzelbild von der Stelle `bei` (S22) -- dasselbe Bild, das der Film
   * dort zeigt, mit allem, was darüberliegt.
   */
  einzelbild: (projectId: string, bei: number) =>
    request<{ renderId: string }>(`/api/projects/${projectId}/renders`, {
      method: 'POST',
      body: JSON.stringify({ kind: 'bild', bei, deliverToBox: false }),
    }),
  /**
   * Die fehlenden Abschnitte der Vorschau rechnen lassen (7.71b). Der Server
   * sagt, welche es gibt, welche fehlen und was er bestellt hat.
   */
  abschnitteRechnen: (projectId: string, hoechstens = 3) =>
    request<{
      abschnitte: { von: number; bis: number; abdruck: string }[];
      fehlen: number;
      bestellt: { von: number; bis: number; abdruck: string; renderId: string }[];
    }>(`/api/projects/${projectId}/abschnitte`, { method: 'POST', body: JSON.stringify({ hoechstens }) }),
  /**
   * „Rechne den Rest, ich gehe“ (7.71f). Wird beim Weggehen geschickt, nicht
   * auf Knopfdruck -- `keepalive`, damit die Anfrage die Seite überlebt.
   */
  abschnitteWeiter: (projectId: string, rev: number) =>
    request<{ weiter: boolean; rev: number; fehlen?: number; bestellt?: number; grund?: string }>(
      `/api/projects/${projectId}/abschnitte/weiter`,
      { method: 'POST', body: JSON.stringify({ rev }), keepalive: true },
    ),
  abschnitteNichtWeiter: (projectId: string) =>
    request<{ weiter: false }>(`/api/projects/${projectId}/abschnitte/weiter`, { method: 'DELETE' }),
  cancelRender: (renderId: string) => request<{ ok: true }>(`/api/renders/${renderId}/cancel`, { method: 'POST' }),
  retryRender: (renderId: string) =>
    request<{ renderId: string }>(`/api/renders/${renderId}/retry`, { method: 'POST' }),

  transcripts: (projectId: string) => request<{ transcripts: TranscriptSummary[] }>(`/api/projects/${projectId}/transcripts`),
  transcript: (id: string) =>
    request<{ transcript: TranscriptSummary & { data: Transcript } }>(`/api/transcripts/${id}`),
  startTranscription: (assetId: string, language: string) =>
    request<{ transcriptId: string; reused: boolean }>(`/api/assets/${assetId}/transcribe`, {
      method: 'POST',
      body: JSON.stringify({ language }),
    }),
  saveTranscript: (id: string, transcript: Transcript) =>
    request<{ ok: true }>(`/api/transcripts/${id}`, { method: 'PUT', body: JSON.stringify({ transcript }) }),
  /** Untertitel aus einer SRT- oder WebVTT-Datei hereinladen (S21). */
  untertitelLaden: (projectId: string, name: string, inhalt: string, language: string) =>
    request<{ transcriptId: string; segmente: number }>(`/api/projects/${projectId}/untertitel`, {
      method: 'POST',
      body: JSON.stringify({ name, inhalt, language }),
    }),

  /** Stapel-Export (S29): je gewählter Vorlage eine Fassung. */
  stapelExport: (projectId: string, vorlagenIds: string[], deliverToBox: boolean) =>
    request<{ bestellt: { renderId: string; fassung: string }[] }>(`/api/projects/${projectId}/renders/stapel`, {
      method: 'POST',
      body: JSON.stringify({ vorlagenIds, deliverToBox }),
    }),

  /** Notizen zum Projekt (S26). */
  notizenSpeichern: (projectId: string, notizen: string) =>
    request<{ notizen: string }>(`/api/projects/${projectId}/notizen`, {
      method: 'PUT',
      body: JSON.stringify({ notizen }),
    }),

  /** Vorlagen (S24). */
  vorlagen: (art?: VorlagenArt) =>
    request<{ vorlagen: VorlageView[] }>(`/api/vorlagen${art ? `?art=${art}` : ''}`),
  vorlageSichern: (name: string, daten: VorlagenDaten) =>
    request<{ vorlage: VorlageView; ersetzt: boolean }>('/api/vorlagen', {
      method: 'POST',
      body: JSON.stringify({ name, daten }),
    }),
  vorlageLoeschen: (id: string) => request<{ ok: true }>(`/api/vorlagen/${id}`, { method: 'DELETE' }),

  brandKits: () => request<{ brandKits: BrandKitView[] }>('/api/brand-kits'),
  brandKit: (id: string) => request<{ brandKit: BrandKitView }>(`/api/brand-kits/${id}`),
  saveBrandKit: (id: string, name: string, data: BrandKit) =>
    request<{ brandKit: BrandKitView }>(`/api/brand-kits/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name, data }),
    }),
  /** Multipart; the browser sets the boundary, so no content-type here. */
  uploadBrandFile: async (id: string, kind: 'logo' | 'font', file: File) => {
    const form = new FormData();
    form.append('file', file);
    const headers: Record<string, string> = {};
    if (shareToken) headers['x-share-token'] = shareToken;
    const res = await fetch(`/api/brand-kits/${id}/${kind}`, {
      method: 'POST',
      body: form,
      headers,
      credentials: 'same-origin',
    });
    const text = await res.text();
    const body = text ? (JSON.parse(text) as { error?: string; brandKit?: BrandKitView }) : null;
    if (!res.ok) throw new ApiError(res.status, body?.error ?? `Fehler ${res.status}`);
    return body as { brandKit: BrandKitView };
  },
  removeBrandFile: (id: string, kind: 'logo' | 'font') =>
    request<{ brandKit: BrandKitView }>(`/api/brand-kits/${id}/${kind}`, { method: 'DELETE' }),
  /** Nur für Admins (7.219). */
  copyBrandKit: (id: string) => request<{ brandKit: BrandKitView }>(`/api/brand-kits/${id}/kopie`, { method: 'POST' }),
  deleteBrandKit: (id: string) => request<{ ok: true; name: string }>(`/api/brand-kits/${id}`, { method: 'DELETE' }),

  // --- Kommentare am Zeitpunkt --------------------------------------------
  kommentare: (projectId: string) =>
    request<{ kommentare: KommentarView[]; gesehenBis: string | null; neu: number }>(
      `/api/projects/${projectId}/kommentare`,
    ),
  kommentareGesehen: (projectId: string) =>
    request<{ gesehenBis: string }>(`/api/projects/${projectId}/kommentare/gesehen`, { method: 'POST' }),
  kommentarSchreiben: (projectId: string, body: { at: number; text: string; clipId?: string | null; parentId?: string | null; name?: string }) =>
    request<{ kommentar: KommentarView }>(`/api/projects/${projectId}/kommentare`, { method: 'POST', body: JSON.stringify(body) }),
  kommentarErledigt: (id: string, erledigt: boolean) =>
    request<{ kommentar: KommentarView }>(`/api/kommentare/${id}/erledigt`, { method: 'POST', body: JSON.stringify({ erledigt }) }),
  kommentarAendern: (id: string, text: string) =>
    request<{ kommentar: KommentarView }>(`/api/kommentare/${id}`, { method: 'PATCH', body: JSON.stringify({ text }) }),
  kommentarLoeschen: (id: string) => request<{ ok: true }>(`/api/kommentare/${id}`, { method: 'DELETE' }),
  abnahmen: (projectId: string) => request<{ abnahmen: AbnahmeView[] }>(`/api/projects/${projectId}/abnahmen`),
  freigeben: (projectId: string, body: { note?: string; name?: string }) =>
    request<{ abnahme: AbnahmeView }>(`/api/projects/${projectId}/abnahmen`, { method: 'POST', body: JSON.stringify(body) }),
  abnahmeZurueckziehen: (id: string) =>
    request<{ abnahme: AbnahmeView }>(`/api/abnahmen/${id}/zurueckziehen`, { method: 'POST' }),
  verlauf: (projectId: string) =>
    request<{ rev: number; zurueckMoeglich: boolean; schritte: VerlaufSchritt[] }>(
      `/api/projects/${projectId}/verlauf`,
    ),
  verlaufZurueck: (projectId: string, rev: number) =>
    request<{ rev: number; zurueckAuf: number; schritte: number }>(
      `/api/projects/${projectId}/verlauf/${rev}/zurueck`,
      { method: 'POST' },
    ),

  // --- Fahrplan -----------------------------------------------------------
  roadmap: () =>
    request<{ items: RoadmapItem[] }>('/api/roadmap'),
  /** Messwerte vom Gerät (7.69j): der Schnittplatz schickt sie beim Abspielen selbst. */
  /**
   * Ein Gerätebefund -- und er muss **das Verschwinden der Seite überleben**
   * (7.115).
   *
   * Der Befund entsteht genau dann, wenn jemand anhält, und danach passiert
   * oft gleich das Nächste: wegtippen, die App wechseln, den Reiter zumachen.
   * Eine gewöhnliche Anfrage bricht dabei ab, und weil der Absender sie
   * ohnehin nur wegschickt (`void … .catch()`), fällt das niemandem auf: der
   * Befund ist weg, und das Messfenster ist schon zurückgesetzt, also lässt
   * er sich auch nicht nachreichen.
   *
   * Genau so ist am 19. September ein Befund verlorengegangen -- bei einem
   * Zwanzigstel der Wartezeiten lag zwischen dem Anhalten und einem Neuladen
   * fast keine Zeit. Auf Saschas Telefon ist das nicht der Ausnahmefall,
   * sondern der Normalfall, und es sind seine Befunde, auf die 7.69v wartet.
   *
   * `keepalive` heisst: der Browser liefert die Anfrage zu Ende, auch wenn
   * die Seite dahinter längst weg ist. Der Körper ist ein paar hundert Byte,
   * also weit unter der Grenze von 64 kB, die dafür gilt.
   */
  messwerte: (body: { projectId: string | null; ua: string; daten: Record<string, unknown> }) =>
    request<{ ok: true }>('/api/messwerte', { method: 'POST', body: JSON.stringify(body), keepalive: true }),
  roadmapFeedback: (body: { title: string; detail: string; area: string }) =>
    request<{ item: RoadmapItem }>('/api/roadmap/feedback', { method: 'POST', body: JSON.stringify(body) }),
  roadmapImage: (id: string, file: Blob, name: string) => {
    const form = new FormData();
    form.append('file', file, name);
    return request<{ item: RoadmapItem }>(`/api/roadmap/${id}/bild`, { method: 'POST', body: form });
  },
  roadmapPatch: (id: string, body: Record<string, unknown>) =>
    request<{ item: RoadmapItem }>(`/api/roadmap/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  roadmapRemove: (id: string) => request<{ ok: true }>(`/api/roadmap/${id}`, { method: 'DELETE' }),
  roadmapVote: (id: string, value: number) =>
    request<{ item: RoadmapItem }>(`/api/roadmap/${id}/stimme`, { method: 'POST', body: JSON.stringify({ value }) }),
  roadmapComments: (id: string) => request<{ comments: RoadmapComment[] }>(`/api/roadmap/${id}/gedanken`),
  roadmapComment: (id: string, text: string) =>
    request<{ ok: true }>(`/api/roadmap/${id}/gedanken`, { method: 'POST', body: JSON.stringify({ text }) }),
  members: (projectId: string) =>
    request<{
      members: { id: string; name: string; email: string; role: ProjectRole }[];
      shareLinks: { id: string; token: string | null; name: string; role: 'editor' | 'viewer'; expires_at: string | null; created_at: string }[];
    }>(`/api/projects/${projectId}/members`),
  removeMember: (projectId: string, userId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/members/${userId}`, { method: 'DELETE' }),
  addMember: (projectId: string, wer: { email: string } | { userId: string }, role: ProjectRole) =>
    request<{ ok: true }>(`/api/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({ ...wer, role }),
    }),
  /** Wer ein Konto hat und hier noch nicht mitarbeitet -- zum Auswählen. */
  kandidaten: (projectId: string) =>
    request<{ kandidaten: { id: string; name: string; email: string }[] }>(`/api/projects/${projectId}/kandidaten`),
  createShareLink: (projectId: string, role: 'editor' | 'viewer', name = '') =>
    request<{ id: string; token: string; url: string; role: string; name: string }>(
      `/api/projects/${projectId}/share-links`,
      { method: 'POST', body: JSON.stringify({ role, name }) },
    ),
  revokeShareLink: (projectId: string, linkId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/share-links/${linkId}`, { method: 'DELETE' }),
  pushSchluessel: () => request<{ publicKey: string }>('/api/push/schluessel'),
  pushAnmelden: (daten: { endpoint: string; keys: { p256dh: string; auth: string }; geraet?: string }) =>
    request<{ ok: true }>('/api/push/anmelden', { method: 'POST', body: JSON.stringify(daten) }),
  pushAbmelden: (endpoint: string) =>
    request<{ ok: true }>('/api/push/abmelden', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  pushProbe: () => request<{ ok: true; geraete: number }>('/api/push/probe', { method: 'POST' }),
  pushStand: () => request<{ geraete: number }>('/api/push/stand'),
  ordner: () => request<{ ordner: { name: string; anzahl: number }[] }>('/api/ordner'),
  ordnerUmbenennen: (von: string, nach: string) =>
    request<{ verschoben: number; fremd: number; nach: string }>('/api/ordner/umbenennen', {
      method: 'POST',
      body: JSON.stringify({ von, nach }),
    }),
};
